import { Drawer, Form, Select, message } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import DrawerSubmitFooter from '../../common/containers/DrawerSubmitFooter';
import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import OnTraccrNumberInput from '../../common/inputs/OnTraccrNumberInput';
import PropTypes from 'prop-types';
import { getProjectRateFields } from '../../projects/ProjectBillingRate/ProjectBillingRateHelper';

const TYPE_OPTIONS = [
  { label: 'Labour', value: 'labour' },
  { label: 'Equipment', value: 'equipment' },
];

export default function ProjectBillingTemplateAddEditDrawer({
  selectedEntry,
  visible,
  toggle,
  onSubmit,
  isFromTemplate = true,
  divisionId,
}) {
  const [form] = Form.useForm();

  const users = useSelector((state) => state.users.users);
  const divisions = useSelector((state) => state.settings.divisions);
  const positions = useSelector((state) => state.settings.positionNames);
  const equipmentTypes = useSelector((state) => state.equipment.equipmentTypes);
  const equipment = useSelector((state) => state.equipment.equipment);

  const [selectedType, setSelectedType] = useState(null);

  const userOptions = useMemo(() => {
    let filteredUsers = users.filter((user) => user.active);
    if (divisionId) {
      const divisionUsers = divisions[divisionId]?.users ?? new Set();
      filteredUsers = filteredUsers.filter((user) => divisionUsers.has(user.id));
    }
    return filteredUsers.map((user) => (
      { label: user.name, value: user.id }
    ));
  }, [users, divisions, divisionId]);

  const positionOptions = useMemo(() => {
    return positions.map((position) => (
      { label: position.name, value: position.id }
    ));
  }, [positions]);

  const equipmentTypeOptions = useMemo(() => {
    return equipmentTypes.map((equipmentType) => (
      { label: equipmentType.name, value: equipmentType.id }
    ));
  }, [equipmentTypes]);

  const equipmentOptions = useMemo(() => {
    let filteredEquipment = equipment.filter((item) => item.active);
    if (divisionId) {
      filteredEquipment = filteredEquipment.filter((item) => item?.divisionIds?.includes(divisionId));
    }
    return filteredEquipment.map((item) => (
      { label: item.name, value: item.id }
    ));
  }, [equipment, divisionId]);

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      return undefined;
    }
    form.resetFields();
    if (selectedEntry) {
      form.setFieldsValue(selectedEntry);
    }
    return undefined;
  }, [visible, form, selectedEntry]);

  useEffect(() => {
    if (visible && selectedEntry) {
      setSelectedType(selectedEntry.type);
    }

    return () => {
      setSelectedType(null);
    };
  }, [selectedEntry, visible]);

  useEffect(() => {
    form.setFieldsValue({
      type: selectedType,
    });
  }, [selectedType, form]);

  const onSubmitForm = async () => {
    try {
      const values = await form.validateFields();
      await onSubmit(values);
    } catch(error) {
      message.error(error?.message || 'Failed to submit rate');
    }
  };

  if (!visible) return null;

  const entityLabel = isFromTemplate ? 'Template' : 'Rate';

  const labourRateFields = getProjectRateFields({ type: 'labour' });
  const equipmentRateFields = getProjectRateFields({ type: 'equipment' });

  return (
    <Drawer
      title={`${selectedEntry ? 'Edit' : 'Add'} Project Billing ${entityLabel}`}
      visible={visible}
      onClose={toggle}
      width={800}
      destroyOnClose
      keyboard={false}
    >
      <Form form={form}>
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Name is required' }]}
        >
          <OnTraccrTextInput placeholder="Enter Name" />
        </Form.Item>
        <Form.Item
          name="type"
          label="Type"
          rules={[{ required: true, message: 'Type is required' }]}
        >
          <Select options={TYPE_OPTIONS} onChange={setSelectedType} value={selectedType} />
        </Form.Item>
        {selectedType === 'labour' && (
          <>
            <Form.Item name="userIds" label="Users">
              <Select
                options={userOptions}
                mode="multiple"
                optionLabelProp="label"
                placeholder="Select Users"
                allowClear
                showSearch
              />
            </Form.Item>
            <Form.Item name="positionIds" label="Positions">
              <Select
                options={positionOptions}
                mode="multiple"
                optionLabelProp="label"
                placeholder="Select Positions"
                allowClear
                showSearch
              />
            </Form.Item>
            {labourRateFields.map((option) => (
              <Form.Item
                name={option.value}
                label={option.label}
                key={option.value}
              >
                <OnTraccrNumberInput placeholder="Enter Billing Rate" />
              </Form.Item>
            ))}
          </>
        )}
        {selectedType === 'equipment' && (
          <>
            <Form.Item name="equipmentTypeIds" label="Equipment Type">
              <Select
                options={equipmentTypeOptions}
                mode="multiple"
                optionLabelProp="label"
                placeholder="Select Equipment Types"
                allowClear
                showSearch
              />
            </Form.Item>
            <Form.Item name="equipmentIds" label="Equipment">
              <Select
                mode="multiple"
                optionLabelProp="label"
                options={equipmentOptions}
                placeholder="Select Equipment"
                allowClear
                showSearch
              />
            </Form.Item>
            {equipmentRateFields.map((option) => (
              <Form.Item
                name={option.value}
                label={option.label}
                key={option.value}
              >
                <OnTraccrNumberInput placeholder="Enter Billing Rate" />
              </Form.Item>
            ))}
          </>
        )}
      </Form>
      <DrawerSubmitFooter
        onSubmit={onSubmitForm}
        onClose={toggle}
      />
    </Drawer>
  );
}

ProjectBillingTemplateAddEditDrawer.propTypes = {
  selectedEntry: PropTypes.object,
  visible: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isFromTemplate: PropTypes.bool,
  divisionId: PropTypes.string,
};

ProjectBillingTemplateAddEditDrawer.defaultProps = {
  isFromTemplate: true,
  divisionId: undefined,
};